import React, { useRef, useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { all_routes } from "../../../router/all_routes"
import ImageWithBasePath from "../../../../core/common/imageWithBasePath"
import DiscountModal from "../discount-modal"
import Table from "../../../../core/common/dataTable/index"
import {
  gender,
  SORT_OPTIONS,
  status,
  STUDENT_STATUS_LABELS,
} from "../../../../core/common/selectoption/selectoption"
import CommonSelect, { Option } from "../../../../core/common/commonSelect"
import TooltipOption from "../../../../core/common/tooltipOption"
import { useSelector } from "react-redux"
import { selectCurrentSession } from "../../../../core/data/redux/slice/sessionsSlice"
import { selectSystemsForCurrentBranch } from "../../../../core/data/redux/slice/systemsSlice"
import { useReactToPrint } from "react-to-print"
import * as XLSX from "xlsx"
import { getAllClasses, Class } from "../../../../service/classes"
import { getAllSections } from "../../../../service/classes"
import {
  getEnrollments,
  Enrollment,
  PaginationData,
  getEnrollmentById,
} from "../../../../service/enrollment"
import { getAllFeeGroups, FEE_GROUP } from "../../../../service/fee-group"
import { listGroups, Group } from "../../../../service/group"
import EditLoginModal from "../student-details/student-login-modal"
import StudentImportModal from "./student-import-modal"
import { selectUser } from "../../../../core/data/redux/slice/user"
import { selectCurrentBranch } from "../../../../core/data/redux/slice/branchesSlice"
import { campusBrandName, campusBranchLabel } from "../../../../utils/campusLogos"

const StudentList = () => {
  const routes = all_routes
  const navigate = useNavigate()
  const currentSession = useSelector(selectCurrentSession)
  const currentBranch = useSelector(selectCurrentBranch)
  const user = useSelector(selectUser)
  const systems = useSelector(selectSystemsForCurrentBranch)
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null)
  const componentRef = useRef<HTMLDivElement>(null)

  const [students, setStudents] = useState<Enrollment[]>([])
  const [loading, setLoading] = useState(true)
  const [classes, setClasses] = useState<Class[]>([])
  const [sectionOptions, setSectionOptions] = useState<Option[]>([])
  const [classesLoading, setClassesLoading] = useState(false)
  const [sectionsLoading, setSectionsLoading] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [sortBy, setSortBy] = useState(SORT_OPTIONS[0])
  const [selectedStudent, setSelectedStudent] = useState<Enrollment | null>(
    null
  )
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const [_feeGroups, setFeeGroups] = useState<
    { label: string; value: string }[]
  >([])
  const [feeGroupsLoading, setFeeGroupsLoading] = useState(false)

  // Academic groups (e.g. Computer/Biology streams on Class IX/X) — only
  // some classes split into groups, so the filter only has anything to show
  // once a class with groups is picked.
  const [groups, setGroups] = useState<Group[]>([])
  const [groupsLoading, setGroupsLoading] = useState(false)

  const [filters, setFilters] = useState<{
    class?: Option
    section?: Option
    group?: Option
    gender?: Option
    status?: Option
  }>({
    class: { label: "Select", value: "" },
    section: { label: "Select", value: "" },
    group: { label: "Select", value: "" },
    gender: { label: "Select", value: "" },
    status: { label: "Select", value: "" },
  })

  const [appliedFilters, setAppliedFilters] = useState({
    page: 1,
    limit: 10,
    session: currentSession?._id,
    system: systems[0]?._id,
    class: "",
    section: "",
    group: "",
    gender: "",
    status: "",
  })

  const [paginationData, setPaginationData] = useState<PaginationData>()
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const isAdminOrOwner = ["admin", "owner"].includes(user?.role?.name?.toLowerCase?.() || "")

  // Keep the applied session/system in sync with the header selection and the
  // loaded systems. Without this, appliedFilters keeps its mount-time snapshot
  // (which is empty right after a reload/campus switch), so the list queries a
  // stale/blank filter and wrongly shows "No data".
  useEffect(() => {
    setAppliedFilters(prev => {
      const session = currentSession?._id
      const system = prev.system || systems[0]?._id
      if (prev.session === session && prev.system === system) return prev
      return { ...prev, session, system }
    })
  }, [currentSession?._id, systems])

  // Depends only on appliedFilters (not currentSession?._id directly) — the
  // sync effect above is the single source of truth for when the session
  // actually changes. Depending on both caused this effect to fire twice per
  // session switch: once immediately with the still-stale appliedFilters
  // (same commit, before the sync effect's setState takes effect), and once
  // more after appliedFilters actually updated. If the first (stale) request
  // resolved after the second (correct) one, it silently overwrote the
  // correct results with the previous session's data — a race condition.
  useEffect(() => {
    fetchEnrollments()
  }, [appliedFilters, searchText, sortBy])

  useEffect(() => {
    fetchClasses()
    fetchSections()
    fetchFeeGroups()
  }, [appliedFilters.system, currentSession?._id])

  const fetchFeeGroups = async () => {
    setFeeGroupsLoading(true)
    try {
      const response = await getAllFeeGroups({
        status: "active",
        system: appliedFilters.system,
      })

      const filteredFeeGroups = response.data
        .filter(
          (group: FEE_GROUP) =>
            !group.name.toLowerCase().includes("admission") &&
            group.status === "active"
        )
        .map((group: FEE_GROUP) => ({
          label: group.name,
          value: group._id,
        }))

      setFeeGroups(filteredFeeGroups)
    } catch (error) {
      console.error("Error fetching fee groups:", error)
      setFeeGroups([])
    } finally {
      setFeeGroupsLoading(false)
    }
  }

  const fetchEnrollments = async () => {
    try {
      setLoading(true)
      const response = await getEnrollments({
        page: appliedFilters.page,
        limit: appliedFilters.limit,
        session: appliedFilters.session,
        system: appliedFilters.system,
        class: appliedFilters.class,
        section: appliedFilters.section,
        group: appliedFilters.group,
        gender: appliedFilters.gender,
        // Someone picking a specific status (Struck off, TC issued...) from
        // the dropdown wants exactly that. Nothing picked defaults to the
        // "on the rolls" set (active + struck-off) — a TC'd, left-without-
        // information, or otherwise departed student isn't the ordinary
        // student list and only shows up when asked for by name.
        admissionStatus: appliedFilters.status || undefined,
        statusScope: appliedFilters.status ? undefined : "onroll",
        searchText: searchText,
        sortBy: sortBy.value?.sortBy,
        sortOrder: sortBy.value?.sortOrder,
      })
      setStudents(response.data)
      setPaginationData(response.pagination)
    } catch (error) {
      console.error("Failed to fetch enrollments:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchSingleEnrollment = async (
    enrollmentId: string
  ): Promise<Enrollment | null> => {
    try {
      const enrollment = await getEnrollmentById(enrollmentId)
      return enrollment
    } catch (error) {
      console.error("Error fetching single enrollment:", error)
      return null
    }
  }

  const handleOpenDiscountModal = async (student: Enrollment) => {
    try {
      setSelectedId(student._id)
      const freshEnrollmentData = await fetchSingleEnrollment(student._id)

      if (freshEnrollmentData) {
        setSelectedStudent(freshEnrollmentData)
      } else {
        setSelectedStudent(student)
      }
    } catch (error) {
      console.error("Failed to fetch enrollment details:", error)
      setSelectedStudent(student)
    }
  }

  const fetchClasses = async () => {
    setClassesLoading(true)
    try {
      const response = await getAllClasses({
        system: appliedFilters.system,
        status: "active",
      })
      setClasses(response.classes)
    } catch (error) {
      console.error("Error fetching classes:", error)
    } finally {
      setClassesLoading(false)
    }
  }

  const fetchGroups = async (classId: string) => {
    if (!classId) {
      setGroups([])
      return
    }
    setGroupsLoading(true)
    try {
      const response = await listGroups({
        system: appliedFilters.system,
        class: classId,
        status: "active",
      })
      setGroups(response?.data || [])
    } catch (error) {
      console.error("Error fetching groups:", error)
      setGroups([])
    } finally {
      setGroupsLoading(false)
    }
  }

  const fetchSections = async () => {
    setSectionsLoading(true)
    try {
      const response = await getAllSections({ session: currentSession?._id })
      const formattedOptions: Option[] = response.sections.map(
        (section: any) => ({
          value: section._id,
          label: section.name,
        })
      )
      setSectionOptions(formattedOptions)
    } catch (error) {
      console.error("Failed to fetch sections:", error)
    } finally {
      setSectionsLoading(false)
    }
  }

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show")
    }

    setAppliedFilters(prev => ({
      ...prev,
      class: filters.class?.value as string,
      section: filters.section?.value as string,
      group: filters.group?.value as string,
      gender: filters.gender?.value as string,
      status: filters.status?.value as string,
      page: 1,
    }))
  }

  const handlePageChange = (page: number, pageSize: number) => {
    setAppliedFilters(prev => ({ ...prev, page, limit: pageSize }))
  }

  const handleResetFilters = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show")
    }

    setFilters({
      class: { label: "Select", value: "" },
      section: { label: "Select", value: "" },
      group: { label: "Select", value: "" },
      gender: { label: "Select", value: "" },
      status: { label: "Select", value: "" },
    })
    setGroups([])
    setAppliedFilters({
      page: 1,
      limit: 10,
      session: currentSession?._id,
      system: systems[0]?._id,
      class: "",
      section: "",
      group: "",
      gender: "",
      status: "",
    })
  }

  const handleSortSelect = (item: {
    label: string
    value: {
      sortBy: string
      sortOrder: string
    }
  }) => {
    setSortBy(item)
  }

  // Full filtered roster, held only while producing the PDF/print output so the
  // export covers every student instead of just the on-screen page. Cleared once
  // the print dialog closes, restoring the normal paginated table.
  const [printAll, setPrintAll] = useState<Enrollment[] | null>(null)
  const [exportPdfLoading, setExportPdfLoading] = useState(false)

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    onAfterPrint: () => setPrintAll(null),
  })

  // Every row matching the current filters — the same query the table runs, just
  // unpaginated. Shared by the Excel and PDF exports.
  const fetchAllForExport = async (): Promise<Enrollment[]> => {
    const total = paginationData?.total || 0
    if (total <= students.length) return students
    try {
      const response = await getEnrollments({
        page: 1,
        limit: total,
        session: appliedFilters.session,
        system: appliedFilters.system,
        class: appliedFilters.class,
        section: appliedFilters.section,
        group: appliedFilters.group,
        gender: appliedFilters.gender,
        admissionStatus: appliedFilters.status || undefined,
        statusScope: appliedFilters.status ? undefined : "onroll",
        searchText: searchText,
        sortBy: sortBy.value?.sortBy,
        sortOrder: sortBy.value?.sortOrder,
      })
      return response.data
    } catch (error) {
      console.error("Failed to fetch all students for export:", error)
      return students
    }
  }

  const handleExportPdf = async () => {
    setExportPdfLoading(true)
    try {
      setPrintAll(await fetchAllForExport())
    } finally {
      setExportPdfLoading(false)
    }
  }

  // Print only once the full roster has rendered into the printable table.
  useEffect(() => {
    if (printAll) handlePrint()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [printAll])

  const handleExportExcel = async () => {
    const fmtDate = (val?: string) => {
      if (!val) return "N/A"
      const d = new Date(val)
      return isNaN(d.getTime()) ? "N/A" : d.toLocaleDateString()
    }

    const allStudents = await fetchAllForExport()

    const adm = (e: typeof allStudents[0]) => (e.admission as any) || {}

    const ws = XLSX.utils.json_to_sheet(
      allStudents.map((enrollment, index) => ({
        // Identity
        "S.No": index + 1,
        "GR Number": enrollment.student?.grNumber || "N/A",
        "First Name": enrollment.user?.firstName || "N/A",
        "Last Name": enrollment.user?.lastName || "N/A",
        "Date of Birth": fmtDate(adm(enrollment).dateOfBirth || enrollment.student?.dateOfBirth),
        Gender: enrollment.user?.gender || adm(enrollment).gender || "N/A",
        "Blood Group": enrollment.user?.bloodGroup || adm(enrollment).bloodGroup || "N/A",
        Religion: enrollment.user?.religion || adm(enrollment).religion || "N/A",
        "Mother Tongue": adm(enrollment).motherTongue || enrollment.student?.motherTongue || "N/A",
        Nationality: adm(enrollment).nationality || "N/A",
        Category: adm(enrollment).category || "N/A",
        // Academic
        System: enrollment.system?.name || "N/A",
        Class: enrollment.class?.name || "N/A",
        Section: enrollment.section?.name || "N/A",
        Status: enrollment.status || "N/A",
        "Date of Join": fmtDate(enrollment.student?.createdAt || enrollment.createdAt),
        "Previous School": adm(enrollment).prevSchoolName || "N/A",
        "Previous Class": adm(enrollment).prevClass || "N/A",
        "Last Result": adm(enrollment).lastResult || "N/A",
        // Father
        "Father Name": enrollment.father?.user?.firstName
          ? `${enrollment.father.user.firstName} ${enrollment.father.user.lastName || ""}`.trim()
          : adm(enrollment).fatherName || enrollment.student?.fatherName || "N/A",
        "Father CNIC": adm(enrollment).fatherCnic || "N/A",
        "Father Phone": enrollment.father?.user?.phoneNumber || adm(enrollment).fatherPhoneNumber || "N/A",
        "Father Email": enrollment.father?.user?.email || adm(enrollment).fatherEmail || "N/A",
        "Father Occupation": adm(enrollment).fatherOccupation || "N/A",
        "Father Company": adm(enrollment).fatherCompanyName || "N/A",
        // Mother
        "Mother Name": enrollment.mother?.user?.firstName
          ? `${enrollment.mother.user.firstName} ${enrollment.mother.user.lastName || ""}`.trim()
          : adm(enrollment).motherName || "N/A",
        "Mother CNIC": adm(enrollment).motherCnic || "N/A",
        "Mother Phone": enrollment.mother?.user?.phoneNumber || adm(enrollment).motherPhoneNumber || "N/A",
        "Mother Email": enrollment.mother?.user?.email || adm(enrollment).motherEmail || "N/A",
        "Mother Occupation": adm(enrollment).motherOccupation || "N/A",
        // Address
        Area: adm(enrollment).area || "N/A",
        City: adm(enrollment).city || "N/A",
        "House No": adm(enrollment).houseNo || "N/A",
        "Block No": adm(enrollment).blockNo || "N/A",
        "Building Name": adm(enrollment).buildingName || "N/A",
        // Medical
        "Has Medical Issue": adm(enrollment).hasMedicalIssue ? "Yes" : "No",
        Allergies: adm(enrollment).allergies || "N/A",
        Medications: adm(enrollment).medications || "N/A",
        // Siblings
        "Has Siblings": adm(enrollment).hasSiblings ? "Yes" : "No",
        "No of Siblings": adm(enrollment).noOfSiblings ?? "N/A",
        // Referrer
        "How Did You Hear": adm(enrollment).referrer || "N/A",
        // Contact
        Email: enrollment.user?.email || "N/A",
      }))
    )
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Students")
    XLSX.writeFile(wb, "students.xlsx")
  }

  const handleOpenLoginModal = (student: Enrollment) => {
    setSelectedStudent(student)
    setShowLoginModal(true)
  }

  const handleCloseLoginModal = () => {
    setShowLoginModal(false)
    setSelectedStudent(null)
  }

  const handleLoginUpdateSuccess = () => {
    fetchEnrollments()
  }

  const columns = [
    {
      // Print-only running serial number (the screen has no need for it, and
      // Excel carries its own). While exporting, the table holds the whole
      // roster on one page so the index is already absolute; the page offset is
      // kept as a fallback for any print that isn't driven by the export.
      title: "S.No",
      width: 70,
      className: "print-col",
      onHeaderCell: () => ({ className: "print-col" } as any),
      render: (_: unknown, __: Enrollment, index: number) =>
        printAll
          ? index + 1
          : ((paginationData?.page || 1) - 1) * (paginationData?.limit || 10) + index + 1,
    },
    {
      title: "GR Number",
      dataIndex: "grNumber",
      render: (text: string, record: Enrollment) => (
        <Link
          to={`${routes.studentDetail.replace(":id", record._id)}`}
          className="link-primary"
        >
          {record?.student?.grNumber}
        </Link>
      ),
      sorter: (a: Enrollment, b: Enrollment) =>
        a.student?.grNumber.localeCompare(b.student?.grNumber),
    },
    {
      title: "Name",
      dataIndex: "user",
      render: (user: any, record: Enrollment) => (
        <div className="d-flex align-items-center">
          <Link
            to={`${routes.studentDetail.replace(":id", record._id)}`}
            className="avatar avatar-md"
          >
            <ImageWithBasePath
              src={"assets/img/students/student-01.jpg"}
              fullSrc={record?.user?.profilePicture}
              className="img-fluid rounded-circle"
              alt="img"
            />
          </Link>
          <div className="ms-2">
            <p className="text-dark mb-0">
              <Link to={`${routes.studentDetail.replace(":id", record._id)}`}>
                {`${record?.user?.firstName || ""} ${record?.user?.lastName || ""}`.trim()}
              </Link>
            </p>
          </div>
        </div>
      ),
      sorter: (a: Enrollment, b: Enrollment) =>
        a.user?.firstName.localeCompare(b.user?.firstName),
    },
    {
      title: "Father Name",
      render: (text: any, record: Enrollment) => {
        return (
          record.father?.user?.firstName ||
          (record.admission as any)?.fatherName ||
          "N/A"
        )
      },

      sorter: (a: Enrollment, b: Enrollment) => {
        const fatherA =
          a.father?.user?.firstName || (a.admission as any)?.fatherName || ""
        const fatherB =
          b.father?.user?.firstName || (b.admission as any)?.fatherName || ""
        return fatherA.localeCompare(fatherB)
      },
    },
    {
      title: "Class",
      dataIndex: ["class", "name"],
      sorter: (a: Enrollment, b: Enrollment) =>
        a.class?.name.localeCompare(b.class?.name),
    },
    {
      title: "Section",
      dataIndex: ["section", "name"],
      sorter: (a: Enrollment, b: Enrollment) =>
        a.section?.name.localeCompare(b.section?.name),
    },
    {
      title: "Gender",
      dataIndex: ["user", "gender"],
      render: (gender: string, record: Enrollment) =>
        record.user?.gender || record.admission?.gender || "N/A",
      sorter: (a: Enrollment, b: Enrollment) =>
        (a.user?.gender || a.admission?.gender || "").localeCompare(
          b.user?.gender || b.admission?.gender || ""
        ),
    },
    {
      title: "Status",
      dataIndex: "status",
      // The student's STANDING, not the enrolment flag. Those two disagree on
      // purpose: a struck-off student keeps an active enrolment so they stay
      // in the defaulters list and the head count while the fees are owed, so
      // reading the enrolment here showed "Active" for a child who had just
      // been struck off.
      render: (_text: string, record: Enrollment) => {
        const status = (record as any).admission?.status || "admitted"
        const label = STUDENT_STATUS_LABELS[status] || "Active"
        return (
          <span
            className={`badge d-inline-flex align-items-center ${
              status === "admitted" ? "badge-soft-success" : "badge-soft-danger"
            }`}
          >
            <i className="ti ti-circle-filled fs-5 me-1"></i>
            {label}
          </span>
        )
      },
      sorter: (a: Enrollment, b: Enrollment) =>
        String((a as any).admission?.status || "").localeCompare(
          String((b as any).admission?.status || "")
        ),
    },
    {
      // Screen-only: hidden on print (roster print uses DOB/Contact/Area instead).
      title: "Email",
      dataIndex: ["user", "email"],
      className: "no-print",
      onHeaderCell: () => ({ className: "no-print" } as any),
      render: (email: string, record: Enrollment) => record.user?.email,
      sorter: (a: Enrollment, b: Enrollment) =>
        (a.user?.email || "").localeCompare(b.user?.email || ""),
    },
    {
      // Print-only columns: hidden on screen (keeps the on-screen table roomy),
      // shown only on the printed roster.
      title: "Date of Birth",
      className: "print-col",
      onHeaderCell: () => ({ className: "print-col" } as any),
      render: (_: unknown, record: Enrollment) => {
        const dob = (record.admission as any)?.dateOfBirth || (record.student as any)?.dateOfBirth
        const d = dob ? new Date(dob) : null
        return d && !isNaN(d.getTime()) ? d.toLocaleDateString("en-GB") : "N/A"
      },
    },
    {
      title: "Contact",
      className: "print-col",
      onHeaderCell: () => ({ className: "print-col" } as any),
      render: (_: unknown, record: Enrollment) =>
        record.father?.user?.phoneNumber ||
        (record.admission as any)?.fatherPhoneNumber ||
        "N/A",
    },
    {
      title: "Area",
      className: "print-col",
      onHeaderCell: () => ({ className: "print-col" } as any),
      render: (_: unknown, record: Enrollment) => (record.admission as any)?.area || "N/A",
    },
    {
      title: "Action",
      dataIndex: "action",
      className: "no-print",
      onHeaderCell: () => ({ className: "no-print" } as any),
      render: (_: unknown, record: Enrollment) => {
        const isDiscountExist = record?.discounts && record.discounts.length > 0
        return (
          <div className="d-flex align-items-center">
            <Link
              to="#"
              className="btn btn-outline-light bg-white btn-icon d-flex align-items-center justify-content-center rounded-circle p-0 me-2"
            >
              <i className="ti ti-phone" />
            </Link>
            <Link
              to={`mailto:${record?.user?.email}`}
              className="btn btn-outline-light bg-white btn-icon d-flex align-items-center justify-content-center rounded-circle p-0 me-3"
            >
              <i className="ti ti-mail" />
            </Link>
            {/* <Link
            to="#"
            data-bs-toggle="modal"
            data-bs-target="#create_fee_modal"
            className="btn btn-light fs-12 fw-semibold me-3"
            onClick={() => handleOpenDiscountModal(record)}
          >
            Collect Fees
          </Link> */}
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                {/* <li>
                <Link
                  className="dropdown-item rounded-1"
                  to={routes.editStudent}
                  state={record}
                >
                  <i className="ti ti-edit-circle me-2" />
                  Edit
                </Link>
              </li> */}
                {/* <li>
                <Link
                  className="dropdown-item rounded-1"
                  to="#"
                  data-bs-toggle="modal"
                  data-bs-target="#delete-modal"
                >
                  <i className="ti ti-trash-x me-2" />
                  Delete
                </Link>
              </li> */}
                {(!user.permissions?.length ||
                  user.permissions?.includes(
                    isDiscountExist
                      ? "profile-discount.edit"
                      : "profile-discount.create"
                  )) && (
                  <li>
                    <Link
                      className="dropdown-item rounded-1"
                      to="#"
                      data-bs-toggle="modal"
                      data-bs-target="#discount-modal"
                      onClick={() => handleOpenDiscountModal(record)}
                    >
                      <i className="ti ti-discount me-2" />
                      {feeGroupsLoading
                        ? "Loading..."
                        : isDiscountExist
                        ? "Update Discount"
                        : "Add Discount"}
                    </Link>
                  </li>
                )}
                <li>
                  <button
                    className="dropdown-item rounded-1 border-0 bg-transparent"
                    onClick={() => handleOpenLoginModal(record)}
                  >
                    <i className="ti ti-lock me-2" />
                    Edit Login Details
                  </button>
                </li>
                {/* Struck off, TC, left without information — and the trail of
                    every previous change. Gated to admin/owner/Principal on the
                    API, so a cashier clicking here gets a clear refusal rather
                    than a silent no-op. */}
                <li>
                  <button
                    className="dropdown-item rounded-1 border-0 bg-transparent"
                    onClick={() =>
                      navigate(`${routes.studentStatus}?studentId=${record.student?._id || ""}`)
                    }
                  >
                    <i className="ti ti-user-exclamation me-2" />
                    Student Status
                  </button>
                </li>
              </ul>
            </div>
          </div>
        )
      },
    },
  ]

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Students List</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">Students</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    All Students
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              {isAdminOrOwner && (
                <button
                  type="button"
                  className="btn btn-outline-light bg-white me-2"
                  onClick={() => setShowImportModal(true)}
                >
                  <i className="ti ti-file-spreadsheet me-1" />
                  Upload Excel
                </button>
              )}
              <TooltipOption
                onRefresh={fetchEnrollments}
                onExportPdf={handleExportPdf}
                onExportExcel={handleExportExcel}
                exportPdfLoading={exportPdfLoading}
              />
            </div>
          </div>
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Students List</h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="mb-3 me-2">
                  <CommonSelect
                    className="select"
                    options={systems}
                    value={systems.find(
                      item => item._id === appliedFilters.system
                    )}
                    getOptionValue={item => item._id}
                    getOptionLabel={item => item.name}
                    onChange={systemItem =>
                      setAppliedFilters(prev => ({
                        ...prev,
                        system: systemItem?._id,
                        page: 1,
                      }))
                    }
                  />
                </div>
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                    onClick={event => event.stopPropagation()}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 pb-0 border-bottom">
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Class</label>
                              {classesLoading ? (
                                <div>Loading classes...</div>
                              ) : (
                                <CommonSelect
                                  className="select"
                                  options={classes.map(cls => ({
                                    label: cls.name,
                                    value: cls._id,
                                  }))}
                                  value={filters.class}
                                  onChange={classItem => {
                                    setFilters(prev => ({
                                      ...prev,
                                      class: classItem as Option,
                                      group: { label: "Select", value: "" },
                                    }))
                                    fetchGroups((classItem as Option)?.value as string)
                                  }}
                                />
                              )}
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Section</label>
                              {sectionsLoading ? (
                                <div>Loading sections...</div>
                              ) : (
                                <CommonSelect
                                  className="select"
                                  options={sectionOptions}
                                  value={filters.section}
                                  onChange={sectionItem =>
                                    setFilters(prev => ({
                                      ...prev,
                                      section: sectionItem as Option,
                                    }))
                                  }
                                />
                              )}
                            </div>
                          </div>
                          {/* Only classes split into academic streams (e.g. Class
                              IX/X -> Computer/Biology) have any groups -- hidden
                              for every other class instead of showing an always-
                              empty dropdown. */}
                          {groups.length > 0 && (
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Group</label>
                                {groupsLoading ? (
                                  <div>Loading groups...</div>
                                ) : (
                                  <CommonSelect
                                    className="select"
                                    options={groups.map(g => ({ label: g.name, value: g._id }))}
                                    value={filters.group}
                                    onChange={groupItem =>
                                      setFilters(prev => ({
                                        ...prev,
                                        group: groupItem as Option,
                                      }))
                                    }
                                  />
                                )}
                              </div>
                            </div>
                          )}
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Gender</label>
                              <CommonSelect
                                className="select"
                                options={gender}
                                value={filters.gender}
                                onChange={genderItem =>
                                  setFilters(prev => ({
                                    ...prev,
                                    gender: genderItem as Option,
                                  }))
                                }
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">Status</label>
                              <CommonSelect
                                className="select"
                                options={status}
                                value={filters.status}
                                onChange={statusItem =>
                                  setFilters(prev => ({
                                    ...prev,
                                    status: statusItem as Option,
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link
                          to="#"
                          className="btn btn-light me-3"
                          onClick={handleResetFilters}
                        >
                          Reset
                        </Link>
                        <div
                          className="btn btn-primary"
                          onClick={handleApplyClick}
                        >
                          Apply
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="d-flex align-items-center bg-white border rounded-2 p-1 mb-3 me-2">
                  <Link
                    to={routes.studentList}
                    className="active btn btn-icon btn-sm me-1 primary-hover"
                  >
                    <i className="ti ti-list-tree" />
                  </Link>
                </div>
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {SORT_OPTIONS.map((item, index) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${
                            item.label === sortBy.label ? "active" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div
              className="card-body p-0 py-3 printable-content"
              ref={componentRef}
            >
              <div className="print-only-header">
                <div className="school-brand">
                  <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                  <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
                </div>
                <div className="report-title">Student List</div>
                <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
              </div>
              <Table
                setSearchText={setSearchText}
                // While exporting, swap in the full roster and size the page to
                // fit it, so the PDF carries every student rather than one page.
                dataSource={printAll ?? students}
                columns={columns}
                loading={loading}
                pagination={
                  printAll
                    ? {
                        ...paginationData,
                        page: 1,
                        limit: printAll.length || 1,
                        total: printAll.length,
                        onChange: handlePageChange,
                      }
                    : { ...paginationData, onChange: handlePageChange }
                }
              />
            </div>
          </div>
        </div>
      </div>

      <DiscountModal
        studentData={selectedStudent}
        fetchData={fetchEnrollments}
        selectedId={selectedId}
        setSelectedId={setSelectedId}
      />

      {showLoginModal && (
        <EditLoginModal
          student={selectedStudent}
          onClose={handleCloseLoginModal}
          onSuccess={handleLoginUpdateSuccess}
        />
      )}

      {showImportModal && (
        <StudentImportModal
          onClose={() => setShowImportModal(false)}
          onImported={fetchEnrollments}
        />
      )}
    </>
  )
}

export default StudentList
